/* RESPONSIBLE TEAM: team-app-security */
import type IntlService from 'ember-intl/services/intl';
import type { ScimGroup, ScimUser, Role, DropdownGroup, TableColumn } from './types';
import { ScimGroupState } from './types';
import { SCIM_TRANSLATION_KEYS } from './constants';

export const SCIM_TABLE_COLUMNS = (intl: IntlService): TableColumn[] => [
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.PRIORITY),
    valuePath: 'priority',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.GROUP_NAME),
    valuePath: 'name',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.STATE),
    valuePath: 'state',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.ROLE),
    valuePath: 'role',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.TABLE.USERS),
    valuePath: 'users',
    sortable: false,
  },
  {
    label: '',
    valuePath: 'actions',
    sortable: false,
  },
];

export const CHANGES_REVIEW_TABLE_COLUMNS = (intl: IntlService): TableColumn[] => [
  {
    label: '',
    valuePath: 'selected',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.CHANGE),
    valuePath: 'change',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.MEMBERSHIP),
    valuePath: 'membership',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.FROM),
    valuePath: 'from',
    sortable: false,
  },
  {
    label: intl.t(SCIM_TRANSLATION_KEYS.CHANGES_REVIEW.TO),
    valuePath: 'to',
    sortable: false,
  },
];

export function getRoleName(
  roleId: string | number | null | undefined,
  roles: Role[],
  intl: IntlService,
): string {
  if (roles.length === 0) {
    return intl.t(SCIM_TRANSLATION_KEYS.TAB.NO_ROLES_AVAILABLE);
  }

  if (!roleId) {
    return intl.t(SCIM_TRANSLATION_KEYS.TAB.NO_ROLE_ASSIGNED);
  }

  let roleIdStr = roleId.toString();
  return (
    roles.find((r) => r?.id === roleIdStr)?.name || intl.t(SCIM_TRANSLATION_KEYS.TAB.UNKNOWN_ROLE)
  );
}

export function getUserInitials(user: ScimUser): string {
  return (
    (user.name?.givenName?.charAt(0)?.toUpperCase() || '') +
    (user.name?.familyName?.charAt(0)?.toUpperCase() || '')
  );
}

export function createRoleDropdownList(
  roles: Role[],
  intl: IntlService,
  onSelectRole: (roleId: number | null) => void,
): DropdownGroup[] {
  if (roles.length === 0) {
    return [
      {
        items: [
          {
            text: intl.t(SCIM_TRANSLATION_KEYS.TAB.NO_ROLES_AVAILABLE),
            onSelectItem: () => {},
          },
        ],
      },
    ];
  }

  let noRoleItem = {
    text: intl.t(SCIM_TRANSLATION_KEYS.TAB.NO_ROLE_ASSIGNED),
    value: 'none',
    onSelectItem: () => onSelectRole(null),
  };

  let roleItems = roles.map((role) => ({
    text: role.name,
    value: role.id,
    onSelectItem: () => onSelectRole(parseInt(role.id, 10)),
  }));

  return [
    {
      items: [noRoleItem, ...roleItems],
    },
  ];
}

export function arraysEqual<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((val, index) => val === b[index]);
}

export function sortGroupsByOrder(groups: ScimGroup[], order: string[]): ScimGroup[] {
  let validGroups = groups.filter((group) => group && group.id !== undefined);

  if (order.length === 0) {
    return [...validGroups].sort((a, b) => a.priority - b.priority);
  }

  let groupsMap = new Map(validGroups.map((group) => [group.id, group]));
  return order.map((id) => groupsMap.get(id)).filter(Boolean) as ScimGroup[];
}

export function getGroupStateDisplayName(state: string | null, intl: IntlService): string {
  if (state === ScimGroupState.ACTIVE) {
    return intl.t(SCIM_TRANSLATION_KEYS.TAB.STATE_ACTIVE);
  }

  if (state === ScimGroupState.INACTIVE) {
    return intl.t(SCIM_TRANSLATION_KEYS.TAB.STATE_INACTIVE);
  }

  return intl.t(SCIM_TRANSLATION_KEYS.TAB.STATE_INACTIVE);
}

export { ScimGroupState };

export function getGroupStateColor(state: string | null): 'green' | 'gray' {
  if (state === ScimGroupState.ACTIVE) {
    return 'green';
  }

  return 'gray';
}
